import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTour } from './TourContext'
import TourCard from './TourCard'

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

const PADDING = 10
const RADIUS = 12

function isElementVisible(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  const style = window.getComputedStyle(el)
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0'
  )
}

export default function TourOverlay() {
  const { isActive, currentStep, cancelTour } = useTour()
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)
  const [visible, setVisible] = useState(false)
  const [vw, setVw] = useState(window.innerWidth)
  const [vh, setVh] = useState(window.innerHeight)

  const updateRect = useCallback(() => {
    if (!currentStep) return
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(`#${currentStep.targetId}`))
    const el = candidates.find(isElementVisible) ?? null
    if (el) {
      const rect = el.getBoundingClientRect()
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height })
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    } else {
      setTargetRect(null)
    }
  }, [currentStep])

  useEffect(() => {
    if (!isActive || !currentStep) {
      setVisible(false)
      setTargetRect(null)
      return
    }

    setVisible(false)
    const timer = setTimeout(() => {
      updateRect()
      setVisible(true)
    }, 120)

    const onResize = () => {
      setVw(window.innerWidth)
      setVh(window.innerHeight)
      updateRect()
    }
    window.addEventListener('resize', onResize)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', onResize)
    }
  }, [isActive, currentStep, updateRect])

  if (!isActive || !currentStep) return null

  const spotlight = targetRect
    ? {
        x: targetRect.left - PADDING,
        y: targetRect.top - PADDING,
        w: targetRect.width + PADDING * 2,
        h: targetRect.height + PADDING * 2,
      }
    : null

  // SVG clip path string — full screen rect with spotlight hole cut out
  const clipPath = spotlight
    ? `M 0 0 H ${vw} V ${vh} H 0 Z M ${spotlight.x + RADIUS} ${spotlight.y} H ${spotlight.x + spotlight.w - RADIUS} Q ${spotlight.x + spotlight.w} ${spotlight.y} ${spotlight.x + spotlight.w} ${spotlight.y + RADIUS} V ${spotlight.y + spotlight.h - RADIUS} Q ${spotlight.x + spotlight.w} ${spotlight.y + spotlight.h} ${spotlight.x + spotlight.w - RADIUS} ${spotlight.y + spotlight.h} H ${spotlight.x + RADIUS} Q ${spotlight.x} ${spotlight.y + spotlight.h} ${spotlight.x} ${spotlight.y + spotlight.h - RADIUS} V ${spotlight.y + RADIUS} Q ${spotlight.x} ${spotlight.y} ${spotlight.x + RADIUS} ${spotlight.y} Z`
    : `M 0 0 H ${vw} V ${vh} H 0 Z`

  return createPortal(
    <div
      className="fixed inset-0"
      style={{ zIndex: 9998 }}
      aria-modal="true"
      role="dialog"
    >
      {/* Dimmed overlay with spotlight cutout */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.35s ease' }}
        width={vw}
        height={vh}
      >
        <path
          d={clipPath}
          fill="rgba(0,0,0,0.68)"
          fillRule="evenodd"
          style={{ transition: 'd 0.4s ease' }}
        />
      </svg>

      {/* Spotlight glow ring */}
      {spotlight && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: spotlight.y,
            left: spotlight.x,
            width: spotlight.w,
            height: spotlight.h,
            borderRadius: RADIUS,
            boxShadow: '0 0 0 2px rgba(255,255,255,0.55), 0 0 24px 4px rgba(255,255,255,0.12)',
            opacity: visible ? 1 : 0,
            transition: 'all 0.4s ease',
          }}
        />
      )}

      {/* Click-blocker — prevent interaction with background */}
      <div
        className="absolute inset-0"
        style={{ pointerEvents: 'all' }}
        onClick={e => e.stopPropagation()}
      />

      {/* Tour Card */}
      {targetRect && (
        <TourCard targetRect={targetRect} visible={visible} />
      )}

      {/* Keyboard: Escape to cancel */}
      <div
        tabIndex={-1}
        className="sr-only"
        onKeyDown={e => { if (e.key === 'Escape') cancelTour() }}
      />
    </div>,
    document.body
  )
}
